import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Calendar, Clock, User, DollarSign, CheckCircle, AlertCircle, CreditCard, Wallet } from 'lucide-react';
import serviceAPI from '../services/serviceAPI';
import appointmentService from '../services/appointmentService';
import paymentService from '../services/paymentService';
import api from '../services/api';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

const BookingPage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useSelector((state) => state.auth);
  const [services, setServices] = useState([]);
  const [barbers, setBarbers] = useState([]);
  const [selectedServices, setSelectedServices] = useState([]);
  const [selectedBarber, setSelectedBarber] = useState(null);
  const [selectedDate, setSelectedDate] = useState('');
  const [availableSlots, setAvailableSlots] = useState([]);
  const [bookedSlots, setBookedSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [loading, setLoading] = useState(false);
  const [bookingStep, setBookingStep] = useState(1);
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [processingPayment, setProcessingPayment] = useState(false);
  const [confirmedBooking, setConfirmedBooking] = useState(null);

  // Helper function to format time as AM/PM
  const formatTimeAMPM = (time) => {
    const [hours, minutes] = time.split(':').map(Number);
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    return `${displayHours}:${String(minutes).padStart(2, '0')} ${ampm}`;
  };

  // Calculate total price for selected services
  const getTotalPrice = () => {
    return selectedServices.reduce((total, serviceId) => {
      const service = services.find(s => s._id === serviceId);
      return total + (service?.discountPrice || service?.price || 0);
    }, 0);
  };

  // Calculate total duration for selected services
  const getTotalDuration = () => {
    return selectedServices.reduce((total, serviceId) => {
      const service = services.find(s => s._id === serviceId);
      return total + (service?.duration || 0);
    }, 0);
  };

  // Toggle service selection
  const toggleServiceSelection = (serviceId) => {
    setSelectedServices(prev => {
      if (prev.includes(serviceId)) {
        return prev.filter(id => id !== serviceId);
      } else {
        return [...prev, serviceId];
      }
    });
  };

  useEffect(() => {
    if (!isAuthenticated) {
      toast.error('Please login to book an appointment');
      navigate('/login');
      return;
    }
    fetchServices();
    fetchBarbers();
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    if (selectedServices.length > 0 && selectedBarber && selectedDate) {
      fetchAvailableSlots();
    }
  }, [selectedServices, selectedBarber, selectedDate]);

  const fetchServices = async () => {
    try {
      const response = await serviceAPI.getAllServices();
      setServices(response.data || []);
    } catch (error) {
      console.error('Failed to fetch services:', error);
      toast.error('Failed to fetch services');
    }
  };

  const fetchBarbers = async () => {
    try {
      // Fetch real barbers from API with correct MongoDB ObjectIds
      const response = await api.get('/barber');
      
      let barberList = [];
      if (response.data && response.data.barbers && Array.isArray(response.data.barbers)) {
        barberList = response.data.barbers;
      } else if (Array.isArray(response.data)) {
        barberList = response.data;
      }
      
      // Filter to only active barbers
      const activeBarbers = barberList.filter(b => b.role === 'barber' && b.isActive !== false);
      
      if (activeBarbers.length > 0) {
        setBarbers(activeBarbers);
      } else {
        console.warn('No barbers found in database');
        setBarbers([]);
      }
    } catch (error) {
      console.error('Failed to fetch barbers:', error);
      toast.error('Failed to fetch barbers');
    }
  };

  const fetchAvailableSlots = async () => {
    try {
      setLoading(true);
      const formattedDate = new Date(selectedDate).toISOString().split('T')[0];
      const response = await appointmentService.getAvailableSlots(
        selectedBarber,
        selectedServices[0],
        formattedDate
      );
      setAvailableSlots(response.data?.slots || []);
      setBookedSlots(response.data?.bookedSlots || []);
      if (!response.data?.slots || response.data.slots.length === 0) {
        if (response.data?.bookedSlots && response.data.bookedSlots.length > 0) {
          toast('All slots are booked for this date', { icon: '⏰' });
        } else {
          toast('No available slots for selected date', { icon: '⏰' });
        }
      }
    } catch (error) {
      console.error('Failed to fetch slots:', error);
      toast.error('Failed to fetch available slots');
      setAvailableSlots([]);
      setBookedSlots([]);
    } finally {
      setLoading(false);
    }
  };

  const handleBooking = async () => {
    if (selectedServices.length === 0 || !selectedBarber || !selectedDate || !selectedSlot) {
      toast.error('Please fill in all fields');
      return;
    }

    if (!paymentMethod) {
      toast.error('Please select a payment method');
      return;
    }

    setLoading(true);
    try {
      // Create appointments for each selected service
      const bookingPromises = selectedServices.map(async (serviceId) => {
        return await appointmentService.bookAppointment({
          service: serviceId,
          barber: selectedBarber,
          appointmentDate: selectedDate,
          startTime: selectedSlot,
          endTime: calculateEndTime(selectedSlot),
          paymentMethod: paymentMethod,
        });
      });

      const responses = await Promise.all(bookingPromises);
      const firstResponse = responses[0];
      const appointmentId = firstResponse.appointment?._id || firstResponse._id;

      // Get first service details for confirmation
      const firstService = services.find(s => s._id === selectedServices[0]);
      const barber = barbers.find(b => b._id === selectedBarber);

      // Store booking data
      setConfirmedBooking({
        servicesCount: selectedServices.length,
        barberName: barber.firstName + ' ' + barber.lastName,
        date: selectedDate,
        time: selectedSlot,
        amount: getTotalPrice(),
        paymentMethod: paymentMethod,
      });

      // If online payment is selected, proceed with payment
      if (paymentMethod === 'stripe' || paymentMethod === 'razorpay') {
        await handleOnlinePayment(appointmentId);
      } else {
        // Cash payment - just show success
        toast.success(`${selectedServices.length} appointment(s) booked successfully! ✅`);
        setBookingStep(5);
        setTimeout(() => {
          navigate('/appointments');
        }, 2000);
      }
    } catch (error) {
      console.error('Booking error:', error);
      toast.error(error.response?.data?.message || 'Booking failed');
    } finally {
      setLoading(false);
    }
  };

  const handleOnlinePayment = async (appointmentId) => {
    setProcessingPayment(true);
    try {
      const amount = getTotalPrice();

      if (paymentMethod === 'razorpay') {
        await processRazorpayPayment(appointmentId, amount);
      } else if (paymentMethod === 'stripe') {
        await processStripePayment(appointmentId, amount);
      }
    } catch (error) {
      console.error('Payment error:', error);
      
      // Check if error is due to missing credentials
      if (error.response?.data?.message?.includes('not configured') || 
          error.response?.data?.message?.includes('credentials') ||
          error.message?.includes('not configured')) {
        toast.error('Online payment is not available at the moment. Please select "Pay at Salon" option.');
      } else {
        toast.error('Payment failed. Please select "Pay at Salon" option or try again.');
      }
    } finally {
      setProcessingPayment(false);
    }
  };

  const processRazorpayPayment = async (appointmentId, amount) => {
    try {
      // Load Razorpay script
      const scriptLoaded = await paymentService.loadRazorpayScript();
      if (!scriptLoaded) {
        toast.error('Failed to load Razorpay. Please select "Pay at Salon" option.');
        return;
      }

      // Create Razorpay order
      const orderData = await paymentService.createRazorpayOrder(appointmentId, amount);

      const options = {
        key: orderData.keyId,
        amount: orderData.amount,
        currency: orderData.currency,
        name: 'Salon Booking',
        description: `${selectedServices.length} Service(s)`,
        order_id: orderData.orderId,
        handler: async function (response) {
          try {
            // Verify payment
            await paymentService.verifyRazorpayPayment({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              paymentId: orderData.paymentId,
            });

            toast.success('Payment successful! ✅');
            setBookingStep(5);
            setTimeout(() => {
              navigate('/appointments');
            }, 2000);
          } catch (error) {
            toast.error('Payment verification failed');
          }
        },
        prefill: {
          name: user?.firstName + ' ' + user?.lastName,
          email: user?.email,
          contact: user?.phone || '',
        },
        theme: {
          color: '#2563eb',
        },
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();
    } catch (error) {
      console.error('Razorpay error:', error);
      if (error.response?.data?.message?.includes('not configured') || 
          error.response?.data?.message?.includes('Razorpay')) {
        toast.error('Razorpay is not configured. Please pay at salon.');
      }
      throw error;
    }
  };

  const processStripePayment = async (appointmentId, amount) => {
    try {
      // Create payment intent
      const paymentData = await paymentService.createStripePayment(appointmentId, amount);

      // Load Stripe
      const stripe = await paymentService.loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY || 'pk_test_placeholder');

      // Redirect to Stripe Checkout or use Stripe Elements
      // For simplicity, we'll show a message
      toast.success('Redirecting to Stripe...');
      
      // In a production app, you'd implement Stripe Elements or Checkout here
      // For now, we'll simulate success after a delay
      setTimeout(async () => {
        try {
          await paymentService.verifyStripePayment(paymentData.clientSecret, paymentData.paymentId);
          toast.success('Payment successful! ✅');
          setBookingStep(5);
          setTimeout(() => {
            navigate('/appointments');
          }, 2000);
        } catch (error) {
          toast.error('Payment verification failed');
        }
      }, 2000);
    } catch (error) {
      console.error('Stripe error:', error);
      throw error;
    }
  };

  const calculateEndTime = (startTime) => {
    if (!startTime || selectedServices.length === 0) return '';
    const [hours, mins] = startTime.split(':').map(Number);
    const duration = getTotalDuration() || 30;
    const endHours = Math.floor((hours * 60 + mins + duration) / 60) % 24;
    const endMins = (hours * 60 + mins + duration) % 60;
    return `${String(endHours).padStart(2, '0')}:${String(endMins).padStart(2, '0')}`;
  };

  const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  // Booking confirmation screen
  if (bookingStep === 5 && confirmedBooking) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center py-12 px-4">
        <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-8 text-center">
          <div className="mb-6 flex justify-center">
            <CheckCircle className="w-16 h-16 text-green-500" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">Booking Confirmed!</h1>
          <p className="text-gray-600 dark:text-gray-400 mb-6">Your appointment has been successfully booked. You will be redirected shortly...</p>
          <div className="bg-gray-100 dark:bg-gray-700 rounded-lg p-4 mb-6 text-left">
            <div className="mb-3">
              <p className="text-sm text-gray-600 dark:text-gray-400">Services</p>
              <p className="font-semibold text-gray-900 dark:text-white">
                {confirmedBooking.servicesCount} service{confirmedBooking.servicesCount > 1 ? 's' : ''} selected
              </p>
            </div>
            <div className="mb-3">
              <p className="text-sm text-gray-600 dark:text-gray-400">Barber</p>
              <p className="font-semibold text-gray-900 dark:text-white">{confirmedBooking.barberName}</p>
            </div>
            <div className="mb-3">
              <p className="text-sm text-gray-600 dark:text-gray-400">Date & Time</p>
              <p className="font-semibold text-gray-900 dark:text-white">
                {new Date(confirmedBooking.date).toLocaleDateString()} at {confirmedBooking.time}
              </p>
            </div>
            <div className="mb-3">
              <p className="text-sm text-gray-600 dark:text-gray-400">Payment Method</p>
              <p className="font-semibold text-gray-900 dark:text-white capitalize">
                {confirmedBooking.paymentMethod === 'cash' ? 'Pay at Salon' : confirmedBooking.paymentMethod}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Total Amount</p>
              <p className="font-bold text-blue-600 text-lg">₹{confirmedBooking.amount}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 py-12 px-4">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-5xl font-bold text-gray-900 dark:text-white mb-4">Book Your Appointment</h1>
          <p className="text-xl text-gray-600 dark:text-gray-400">Select your preferred service, barber, date, and time</p>
        </div>

        {/* Progress Steps */}
        <div className="flex justify-center mb-12 gap-4">
          {[1, 2, 3, 4].map((step) => (
            <div key={step} className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-white transition ${
                step <= bookingStep ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'
              }`}>
                {step}
              </div>
              <span className="text-sm font-medium text-gray-600 dark:text-gray-400 hidden sm:inline">
                {step === 1 ? 'Service' : step === 2 ? 'Barber' : step === 3 ? 'Date & Time' : 'Payment'}
              </span>
              {step < 4 && <div className={`w-8 h-1 transition ${step < bookingStep ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'}`}></div>}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Booking Form */}
          <div className="lg:col-span-2 space-y-6">
            {/* Step 1: Select Services (Multiple) */}
            {bookingStep >= 1 && (
              <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-8 border-2 border-transparent hover:border-blue-400 transition">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <DollarSign className="w-6 h-6 text-blue-600" />
                    <h2 className="text-2xl font-bold dark:text-white">Select Services</h2>
                  </div>
                  {selectedServices.length > 0 && (
                    <span className="text-sm bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 px-3 py-1 rounded-full font-semibold">
                      {selectedServices.length} selected
                    </span>
                  )}
                </div>
                {services.length > 0 ? (
                  <>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                      {services.map((s) => (
                        <div
                          key={s._id}
                          onClick={() => toggleServiceSelection(s._id)}
                          className={`p-4 rounded-xl border-2 transition transform hover:scale-105 cursor-pointer relative ${
                            selectedServices.includes(s._id)
                              ? 'border-blue-600 bg-blue-50 dark:bg-blue-900/50 shadow-lg'
                              : 'border-gray-300 dark:border-gray-600 hover:border-blue-400'
                          }`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <p className="font-semibold text-gray-900 dark:text-white mb-2">{s.name}</p>
                              <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">{s.duration} mins</p>
                              <p className="text-blue-600 font-bold text-lg">₹{s.discountPrice || s.price}</p>
                              {s.discountPrice && (
                                <p className="text-xs text-gray-500 line-through">₹{s.price}</p>
                              )}
                            </div>
                            <div className={`w-6 h-6 rounded-md border-2 flex items-center justify-center transition ${
                              selectedServices.includes(s._id)
                                ? 'bg-blue-600 border-blue-600'
                                : 'border-gray-300 dark:border-gray-600'
                            }`}>
                              {selectedServices.includes(s._id) && (
                                <CheckCircle className="w-5 h-5 text-white" />
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    {selectedServices.length > 0 && (
                      <button
                        onClick={() => setBookingStep(2)}
                        className="w-full bg-blue-600 text-white py-3 rounded-xl font-semibold hover:bg-blue-700 transition"
                      >
                        Continue with {selectedServices.length} service{selectedServices.length > 1 ? 's' : ''}
                      </button>
                    )}
                  </>
                ) : (
                  <p className="text-gray-500 dark:text-gray-400 text-center py-8">Loading services...</p>
                )}
              </div>
            )}

            {/* Step 2: Select Barber */}
            {bookingStep >= 2 && selectedServices.length > 0 && (
              <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-8 border-2 border-transparent hover:border-blue-400 transition">
                <div className="flex items-center gap-3 mb-6">
                  <User className="w-6 h-6 text-blue-600" />
                  <h2 className="text-2xl font-bold dark:text-white">Select Barber</h2>
                </div>
                <div className="space-y-3">
                  {barbers.map((b) => (
                    <button
                      key={b._id}
                      onClick={() => {
                        setSelectedBarber(b._id);
                        setBookingStep(3);
                      }}
                      className={`w-full p-4 rounded-xl border-2 transition text-left flex items-center justify-between ${
                        selectedBarber === b._id
                          ? 'border-blue-600 bg-blue-50 dark:bg-blue-900/50 shadow-lg'
                          : 'border-gray-300 dark:border-gray-600 hover:border-blue-400'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        {b.avatar ? (
                          <img 
                            src={b.avatar} 
                            alt={b.firstName}
                            className="w-12 h-12 rounded-full object-cover border-2 border-gray-300 dark:border-gray-500"
                          />
                        ) : (
                          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold text-lg">
                            {b.firstName[0]}{b.lastName[0]}
                          </div>
                        )}
                        <div>
                          <p className="font-semibold text-gray-900 dark:text-white">{b.firstName} {b.lastName}</p>
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            ⭐ {b.rating} ({b.reviewCount} reviews)
                          </p>
                        </div>
                      </div>
                      {selectedBarber === b._id && <CheckCircle className="w-6 h-6 text-green-500" />}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Step 3: Select Date & Time */}
            {bookingStep >= 3 && selectedServices.length > 0 && selectedBarber && (
              <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-8 border-2 border-transparent hover:border-blue-400 transition">
                <div className="flex items-center gap-3 mb-6">
                  <Calendar className="w-6 h-6 text-blue-600" />
                  <h2 className="text-2xl font-bold dark:text-white">Select Date & Time</h2>
                </div>

                <div className="mb-8">
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Choose Date</label>
                  <input
                    type="date"
                    value={selectedDate}
                    onChange={(e) => {
                      setSelectedDate(e.target.value);
                      setSelectedSlot(null);
                      setAvailableSlots([]);
                      setBookedSlots([]);
                    }}
                    min={getTodayDate()}
                    className="w-full px-4 py-3 border-2 border-gray-300 dark:border-gray-600 rounded-xl dark:bg-gray-700 dark:text-white focus:border-blue-600 focus:outline-none transition"
                  />
                </div>

                {selectedDate && (
                  <>
                    {loading ? (
                      <div className="flex flex-col items-center justify-center py-12">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
                        <p className="text-gray-600 dark:text-gray-400">Finding available slots...</p>
                      </div>
                    ) : availableSlots.length > 0 || bookedSlots.length > 0 ? (
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4">
                          <Clock className="inline w-5 h-5 mr-2" />
                          Time Slots
                        </label>
                        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
                          {availableSlots.map((slot) => (
                            <button
                              key={slot}
                              onClick={() => setSelectedSlot(slot)}
                              className={`p-3 rounded-lg text-sm font-semibold transition transform hover:scale-105 ${
                                selectedSlot === slot
                                  ? 'bg-blue-600 text-white shadow-lg'
                                  : 'bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white hover:bg-gray-300 dark:hover:bg-gray-600'
                              }`}
                            >
                              {formatTimeAMPM(slot)}
                            </button>
                          ))}
                          {bookedSlots.map((slot) => (
                            <button
                              key={slot}
                              disabled
                              className="p-3 rounded-lg text-sm font-semibold bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 cursor-not-allowed opacity-60"
                              title="This slot is already booked"
                            >
                              {formatTimeAMPM(slot)}
                            </button>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center justify-center py-12 bg-red-50 dark:bg-red-900/20 rounded-xl">
                        <AlertCircle className="w-6 h-6 text-red-600 mr-3" />
                        <p className="text-red-600 dark:text-red-400 font-medium">No available slots for this date. Try another date.</p>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {/* Step 4: Select Payment Method */}
            {bookingStep >= 3 && selectedSlot && (
              <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-8 border-2 border-transparent hover:border-blue-400 transition">
                <div className="flex items-center gap-3 mb-6">
                  <CreditCard className="w-6 h-6 text-blue-600" />
                  <h2 className="text-2xl font-bold dark:text-white">Payment Method</h2>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {/* Cash Payment */}
                  <button
                    onClick={() => setPaymentMethod('cash')}
                    className={`p-6 rounded-xl border-2 transition transform hover:scale-105 ${
                      paymentMethod === 'cash'
                        ? 'border-blue-600 bg-blue-50 dark:bg-blue-900/50 shadow-lg'
                        : 'border-gray-300 dark:border-gray-600 hover:border-blue-400'
                    }`}
                  >
                    <DollarSign className="w-8 h-8 mx-auto mb-3 text-blue-600" />
                    <p className="font-semibold text-gray-900 dark:text-white text-center">Pay at Salon</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 text-center mt-2">Cash payment</p>
                  </button>

                  {/* Razorpay Payment */}
                  <button
                    onClick={() => setPaymentMethod('razorpay')}
                    className={`p-6 rounded-xl border-2 transition transform hover:scale-105 ${
                      paymentMethod === 'razorpay'
                        ? 'border-blue-600 bg-blue-50 dark:bg-blue-900/50 shadow-lg'
                        : 'border-gray-300 dark:border-gray-600 hover:border-blue-400'
                    }`}
                  >
                    <Wallet className="w-8 h-8 mx-auto mb-3 text-blue-600" />
                    <p className="font-semibold text-gray-900 dark:text-white text-center">Razorpay</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 text-center mt-2">UPI, Cards, Wallets</p>
                  </button>

                  {/* Stripe Payment */}
                  <button
                    onClick={() => setPaymentMethod('stripe')}
                    className={`p-6 rounded-xl border-2 transition transform hover:scale-105 ${
                      paymentMethod === 'stripe'
                        ? 'border-blue-600 bg-blue-50 dark:bg-blue-900/50 shadow-lg'
                        : 'border-gray-300 dark:border-gray-600 hover:border-blue-400'
                    }`}
                  >
                    <CreditCard className="w-8 h-8 mx-auto mb-3 text-blue-600" />
                    <p className="font-semibold text-gray-900 dark:text-white text-center">Stripe</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 text-center mt-2">Credit/Debit Cards</p>
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Summary Card */}
          <div>
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-8 sticky top-24 border-2 border-blue-600">
              <h2 className="text-2xl font-bold mb-8 dark:text-white text-center">Order Summary</h2>

              {selectedServices.length > 0 ? (
                <div className="space-y-6">
                  {/* Services Summary */}
                  <div className="pb-6 border-b border-gray-200 dark:border-gray-700">
                    <p className="text-sm text-gray-600 dark:text-gray-400 font-medium mb-3">Services ({selectedServices.length})</p>
                    <div className="space-y-2">
                      {selectedServices.map((serviceId) => {
                        const service = services.find(s => s._id === serviceId);
                        return service ? (
                          <div key={serviceId} className="flex justify-between items-center">
                            <div>
                              <p className="text-sm font-semibold text-gray-900 dark:text-white">{service.name}</p>
                              <p className="text-xs text-gray-500 dark:text-gray-500">{service.duration} minutes</p>
                            </div>
                            <p className="text-sm font-bold text-blue-600">₹{service.discountPrice || service.price}</p>
                          </div>
                        ) : null;
                      })}
                    </div>
                    <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                      <div className="flex justify-between items-center">
                        <p className="text-xs text-gray-500">Total Duration:</p>
                        <p className="text-sm font-semibold text-gray-900 dark:text-white">{getTotalDuration()} mins</p>
                      </div>
                    </div>
                  </div>

                  {/* Barber Summary */}
                  {selectedBarber && (() => {
                    const barber = barbers.find(b => b._id === selectedBarber);
                    return barber ? (
                      <div className="pb-6 border-b border-gray-200 dark:border-gray-700">
                        <p className="text-sm text-gray-600 dark:text-gray-400 font-medium mb-1">Barber</p>
                        <p className="text-lg font-bold text-gray-900 dark:text-white">{barber.firstName} {barber.lastName}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">⭐ {barber.rating}</p>
                      </div>
                    ) : null;
                  })()}

                  {/* Date & Time Summary */}
                  {selectedDate && selectedSlot && (
                    <div className="pb-6 border-b border-gray-200 dark:border-gray-700">
                      <p className="text-sm text-gray-600 dark:text-gray-400 font-medium mb-1">Date & Time</p>
                      <p className="text-lg font-bold text-gray-900 dark:text-white">
                        {new Date(selectedDate).toLocaleDateString('en-US', { 
                          weekday: 'short', 
                          month: 'short', 
                          day: 'numeric' 
                        })}
                      </p>
                      <p className="text-lg font-bold text-blue-600 mt-1">{selectedSlot}</p>
                    </div>
                  )}

                  {/* Price Summary */}
                  <div className="bg-blue-50 dark:bg-blue-900/30 rounded-lg p-4">
                    <div className="flex justify-between items-center mb-2">
                      <p className="font-semibold text-gray-700 dark:text-gray-300">Total Amount</p>
                      <p className="text-3xl font-bold text-blue-600">₹{getTotalPrice()}</p>
                    </div>
                    {selectedServices.length > 1 && (
                      <p className="text-xs text-gray-500">
                        {selectedServices.length} services selected
                      </p>
                    )}
                  </div>
                </div>
              ) : (
                <p className="text-center text-gray-500 dark:text-gray-400 py-8">Select services to see details</p>
              )}

              <button
                onClick={handleBooking}
                disabled={selectedServices.length === 0 || !selectedBarber || !selectedDate || !selectedSlot || !paymentMethod || loading || processingPayment}
                className="w-full bg-gradient-to-r from-blue-600 to-blue-700 text-white py-4 rounded-xl font-bold mt-8 transition transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 shadow-lg"
              >
                {loading || processingPayment ? (
                  <div className="flex items-center justify-center gap-2">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    {processingPayment ? 'Processing Payment...' : 'Booking...'}
                  </div>
                ) : (
                  <>
                    {paymentMethod === 'cash' ? 'Confirm Booking' : `Pay ₹${getTotalPrice()} & Book`}
                  </>
                )}
              </button>

              <p className="text-xs text-gray-500 dark:text-gray-400 text-center mt-4">
                {paymentMethod === 'cash' 
                  ? 'Payment will be collected at the salon' 
                  : `Secure payment via ${paymentMethod === 'razorpay' ? 'Razorpay' : 'Stripe'}`
                }
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingPage;
